import jsPDF from "jspdf";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export const generatePDF = (cliente, empresaConfig) => {
    const doc = new jsPDF();
    
    // Settings
    const margin = 20;
    let yPos = margin;
    const pageWidth = doc.internal.pageSize.width;

    // --- HEADER ---

    // Logo
    if (empresaConfig?.logo_url_base64) {
        try {
            doc.addImage(empresaConfig.logo_url_base64, "JPEG", margin, yPos, 40, 20);
            yPos += 25;
        } catch (e) {
            console.warn("Could not add logo to PDF", e);
        }
    }

    // Company details (Left)
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(empresaConfig?.nombre_empresa || "Tu Empresa", margin, yPos);

    doc.setFont("helvetica", "normal");
    yPos += 5;
    if (empresaConfig?.cif) {
        doc.setTextColor(0, 0, 0);
        doc.text(`CIF/NIF: ${empresaConfig.cif}`, margin, yPos);
        yPos += 5;
    }
    if (empresaConfig?.direccion) {
        doc.setTextColor(0, 0, 0);
        doc.text(empresaConfig.direccion, margin, yPos);
        yPos += 5;
    }
    if (empresaConfig?.telefono) {
        doc.setTextColor(0, 0, 0);
        doc.text(`Tel: ${empresaConfig.telefono}`, margin, yPos);
        yPos += 5;
    }
    if (empresaConfig?.email) {
        doc.setTextColor(0, 0, 0);
        doc.text(`Email: ${empresaConfig.email}`, margin, yPos);
        yPos += 5;
    }

    // Invoice Title and details (Right)
    let rightYPos = margin + (empresaConfig?.logo_url_base64 ? 25 : 0);

    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("FACTURA", pageWidth - margin, rightYPos, { align: "right" });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    rightYPos += 8;
    const numFactura = `F-${new Date().getFullYear()}-${cliente.id.substring(0, 4).toUpperCase()}`;
    doc.setTextColor(0, 0, 0);
    doc.text(`Nº Factura: ${numFactura}`, pageWidth - margin, rightYPos, { align: "right" });

    rightYPos += 5;
    doc.setTextColor(0, 0, 0);
    doc.text(`Fecha: ${format(new Date(), "dd/MM/yyyy")}`, pageWidth - margin, rightYPos, { align: "right" });

    // --- CLIENT DETAILS ---
    yPos = Math.max(yPos, rightYPos) + 15;

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Facturar a:", margin, yPos);

    yPos += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(cliente.nombre || "Cliente / Empresa", margin, yPos);

    doc.setFont("helvetica", "normal");
    if (cliente.dni_cif_nif) {
        yPos += 5;
        doc.setTextColor(0, 0, 0);
        doc.text(`DNI/CIF/NIF: ${cliente.dni_cif_nif}`, margin, yPos);
    }
    if (cliente.email) {
        yPos += 5;
        doc.setTextColor(0, 0, 0);
        doc.text(cliente.email, margin, yPos);
    }

    if (cliente.telefono) {
        yPos += 5;
        doc.setTextColor(0, 0, 0);
        doc.text(`Tel: ${cliente.telefono}`, margin, yPos);
    }
    if (cliente.ubicacion) {
        yPos += 5;
        doc.setTextColor(0, 0, 0);
        doc.text(`Dirección/Ubicación: ${cliente.ubicacion}`, margin, yPos);
    }

    yPos += 15;

    // --- INVOICE LINES (Manual Table) ---
    const tableStartY = yPos;
    const tableData = cliente.lineas_factura || [];
    
    // Table headers
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    
    const headers = ["Concepto", "Cant.", "Precio Unit.", "Subtotal"];
    const colWidths = [pageWidth - margin * 2 - 90, 30, 30, 30];
    let xPos = margin;
    
    headers.forEach((header, index) => {
        // Draw background rectangle first
        doc.setFillColor(14, 77, 88); // Primary color #0E4D58
        doc.rect(xPos, yPos, colWidths[index], 8, 'F');
        
        // Draw border
        doc.setDrawColor(14, 77, 88);
        doc.rect(xPos, yPos, colWidths[index], 8, 'S');
        
        // Draw white text
        doc.setTextColor(255, 255, 255);
        doc.text(header, xPos + 2, yPos + 5);
        
        xPos += colWidths[index];
    });
    
    yPos += 8;

    // Table rows
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(200, 200, 200); // Light gray border for rows
    
    tableData.forEach((linea) => {
        const row = [
            linea.concepto,
            linea.cantidad.toString(),
            `${linea.precio_unitario.toFixed(2)} €`,
            `${linea.subtotal.toFixed(2)} €`
        ];
        
        xPos = margin;
        row.forEach((cell, index) => {
            // Draw border for each cell
            doc.rect(xPos, yPos, colWidths[index], 8, 'S');
            doc.text(cell, xPos + 2, yPos + 5);
            xPos += colWidths[index];
        });
        
        yPos += 8;
    });

    // --- TOTALS ---
    yPos += 10;
    const totalsX = pageWidth - margin - 60;

    // Subtotal
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.text("Subtotal:", totalsX, yPos);
    doc.text(`${(cliente.subtotal || 0).toFixed(2)} €`, pageWidth - margin, yPos, { align: "right" });

    // IVA
    if (cliente.aplicar_iva) {
        yPos += 6;
        doc.setTextColor(0, 0, 0);
        doc.text("IVA (21%):", totalsX, yPos);
        doc.text(`${(cliente.iva || 0).toFixed(2)} €`, pageWidth - margin, yPos, { align: "right" });
    }

    // Total
    yPos += 8;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("TOTAL:", totalsX, yPos);
    doc.text(`${(cliente.total || 0).toFixed(2)} €`, pageWidth - margin, yPos, { align: "right" });

    // Footer
    //const pageHeight = doc.internal.pageSize.height;
    //doc.setFontSize(8);
    //doc.setFont("helvetica", "normal");
    //doc.setTextColor(100);
    //doc.text(
    //    `Documento generado el ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: es })}`,
    //    pageWidth / 2,
    //    pageHeight - 10,
    //    { align: "center" }
    //);

    // Save the PDF
    const safeName = (cliente.nombre || "Cliente").replace(/[^a-z0-9]/gi, "_").toLowerCase();
    doc.save(`Factura_${safeName}_${format(new Date(), "yyyyMMdd")}.pdf`);
};
